import React from 'react';

import { formatPercentValue } from '../../../foundation/utils/percentageHandler';

type EvidenceDisplayProps = {
  evidence: Record<string, unknown>;
};

// Exact evidence labels (per the ACMG evidence spec) whose numeric value is a ratio, not a
// percentage - e.g. "VAF / (PAF + 10 %) = 0.20". These must never get the " %" percent suffix,
// even though their label text happens to contain "VAF"/"PAF"/"FAF"/"DAF" and "%".
// Includes the raw frequency_vaf_paf_comparison() ratio labels too, since arguments without a
// specific per-argument mapping fall back to that full (unrelabeled) evidence dict.
const RATIO_EXACT_LABELS = [
  'VAF / (PAF + 10 %)',
  'FAF / DAF',
  'VAF/(PAF + 10%) calculated',
  'FAF/DAF ratio',
];

// Exact "primary metric" labels built by frequency_evidence_by_argument() (backend). These must
// always be displayed first, per the spec's evidence ordering (e.g. listings 2002/2006/2008/2009:
// VAF, then PAF, then the ratio).
const PRIMARY_METRIC_LABELS = [
  'Variant Allele Frequency total (VAF)',
  'Variant Allele Frequency total filtered (FAF)',
  'Variant Allele Count homo/hemizygous total',
];

const PAF_LABEL_MARKER = 'Most frequent P/LP variant in gene';
const DAF_LABEL_MARKER = 'calculated disease allele frequency (DAF)';

// Postgres' jsonb storage (used for the persisted evidencedata field) does not preserve object
// key insertion order (keys come back ordered by byte length, then lexicographically), so the
// evidence rows must be explicitly re-ordered here to match the spec's required display order:
// primary metric (VAF/FAF/homo-hemi count) -> dynamic comparator (PAF/DAF) -> ratio.
// Any unrecognized label keeps its original relative order.
const getDisplayPriority = (label: string): number => {
  if (PRIMARY_METRIC_LABELS.includes(label)) {
    return 0;
  }
  if (label.startsWith(PAF_LABEL_MARKER) || label.includes(DAF_LABEL_MARKER)) {
    return 1;
  }
  if (RATIO_EXACT_LABELS.includes(label)) {
    return 2;
  }
  return 3;
};

const formatRatio = (value: number): string =>
  Number.isInteger(value)
    ? String(value)
    : value.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');

const formatValue = (label: string, value: unknown): string => {
  if (typeof value === 'number') {
    if (label === 'Revel score') {
      return String(value);
    }
    if (label.includes('fraction')) {
      return value.toString();
    }
    if (RATIO_EXACT_LABELS.includes(label)) {
      return formatRatio(value);
    }
    if (
      label.includes('%') ||
      label.includes('VAF') ||
      label.includes('FAF') ||
      label.includes('PAF') ||
      label.includes('DAF')
    ) {
      const percent = formatPercentValue(value);
      // Per spec, "VAF = 0" (e.g. variant absent from population databases) is shown as a plain
      // 0, not "0 %".
      return percent === '' || percent === '0' ? percent : `${percent} %`;
    }
    return Number.isInteger(value) ? String(value) : value.toFixed(6);
  }

  if (Array.isArray(value) || (value !== null && typeof value === 'object')) {
    return JSON.stringify(value);
  }

  return value == null ? '' : String(value);
};

const EvidenceDisplay = ({ evidence }: EvidenceDisplayProps) => {
  // Stable sort: entries with the same priority (e.g. all-unrecognized labels) keep their
  // original relative order, so this only reorders the known frequency-evidence labels.
  const orderedEntries = Object.entries(evidence)
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const priorityDiff =
        getDisplayPriority(a.entry[0]) - getDisplayPriority(b.entry[0]);
      return priorityDiff !== 0 ? priorityDiff : a.index - b.index;
    })
    .map(({ entry }) => entry);

  return (
    <div className="argument-evidence">
      {orderedEntries.map(([label, value], index) => {
        // The backend already words these as complete, self-contained sentences (e.g. one line
        // per matching ClinVar variant): show each sentence on its own row, with no "label = "
        // prefix. This is a generic, value-shape based rule (any label), not argument-specific.
        if (
          Array.isArray(value) &&
          value.every((item) => typeof item === 'string')
        ) {
          return value.map((line, lineIndex) => (
            <div
              key={`${label}-${index}-${lineIndex}`}
              className="argument-evidence__row"
            >
              {line}
            </div>
          ));
        }
        return (
          <div key={`${label}-${index}`} className="argument-evidence__row">
            <span>{label} = </span>
            <span>{formatValue(label, value)}</span>
          </div>
        );
      })}
    </div>
  );
};

export default EvidenceDisplay;
